import { LocaleContext } from '../../components/LocaleContext';
import {
    buildPreviewPeriodOptionLabels,
    registerPreviewCalendarLocale,
} from '../../utils/previewUiLocale';
import React, { useContext, useState, useEffect, useRef, useCallback, useMemo } from "react";
import { ReactTabulator,reactFormatter } from 'react-tabulator';
import { DatabaseContext } from "../../redux/DatabaseStore";
import '../../assets/css/style.css';
import '../../assets/css/iconsmenu.css';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { MdSearch } from "react-icons/md";
import { MdSearchOff } from "react-icons/md";
import { AddIcon, EditIcon, CopyIcon, DeleteIcon, StepBackIcon, PrintIcon, SearchIcon, ExportIcon, VerticalLineIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon, PriceIcon, CalendarIcon, EditRowIcon, RefreshIcon } from "../svg/svgIcons";
import { deleteRequest, getRequest,postRequest } from '../../api/ApiController';
import { useReactToPrint } from "react-to-print";
import { DateTime } from "luxon";
import { PanelMenu } from 'primereact/panelmenu';
import { Checkbox } from 'primereact/checkbox';
import UtilContext from "../../components/UtilContext";
import { getModuleSettingValue } from '../../utils/moduleSettings';
import IntersectionObserverWrapper from '../../components/toolbar/IntersectionObserverWrap';
import { OverflowMenuProvider } from '../../components/toolbar/OverflowMenu';
import { Chart } from 'primereact/chart';
import { Calendar } from '../../components/PrimeCalendar';
import { Skeleton } from 'primereact/skeleton';
import { addLocale } from 'primereact/api';
import {
  PREVIEW_PERIOD_DEFAULT,
  PREVIEW_PERIOD_OPTIONS,
  defaultDateForPeriod,
  datePickerPropsForPeriod,
  toLocalDateString,
  parseLocalDateString,
} from '../../utils/previewPeriodSelection';
import {
  EMPTY_OTK_PREVIEW_MODEL,
  normalizeOtkPreviewStats,
  otkPreviewLegendCount,
  otkPreviewTotal,
} from '../../utils/otkPreviewStatsModel';

const PREVIEW_PAGE = 'OTKPreview';

function parseSavedPeriod(attrs) {
  if (!attrs?.preview_period || typeof attrs.preview_period.timePeriod !== 'string') return null;
  if (!PREVIEW_PERIOD_OPTIONS.includes(attrs.preview_period.timePeriod)) return null;
  const { timePeriod } = attrs.preview_period;
  const date = attrs.preview_period.date;
  if (timePeriod === 'Všetko') return { timePeriod, date: null };
  if (timePeriod === 'Vlastné' && Array.isArray(date) && date.length >= 2) {
    return {
      timePeriod,
      date: date.map((d) => {
        if (d == null) return null;
        if (typeof d === 'string') return parseLocalDateString(d);
        return d instanceof Date ? d : parseLocalDateString(String(d));
      }),
    };
  }
  if (date != null) {
    const d = typeof date === 'string' ? parseLocalDateString(date) : (date instanceof Date ? date : parseLocalDateString(String(date)));
    if (d && !Number.isNaN(d.getTime())) return { timePeriod, date: d };
  }
  return null;
}

function serializePeriod(timePeriod, date) {
  const payload = { timePeriod };
  if (date == null) payload.date = null;
  else if (Array.isArray(date)) payload.date = date.map((d) => (d instanceof Date ? toLocalDateString(d) : d));
  else payload.date = date instanceof Date ? toLocalDateString(date) : date;
  return payload;
}

export default function PreviewOTK() {
    const { t, locale } = useContext(LocaleContext);
  const periodOptionLabels = useMemo(() => buildPreviewPeriodOptionLabels(t), [t]);
  const [calendarLocale, setCalendarLocale] = useState(() => registerPreviewCalendarLocale(addLocale, locale, t));
  useEffect(() => { setCalendarLocale(registerPreviewCalendarLocale(addLocale, locale, t)); }, [locale, t]);

  const [state,dispatch] = useContext(DatabaseContext);
  const { setUserMaskFilter } = useContext(UtilContext);

  const [timePeriod,setTimePeriod] = useState(PREVIEW_PERIOD_DEFAULT);
  const [date,setDate] = useState(null);
  const [dateProps,setDateProps] = useState(datePickerPropsForPeriod(PREVIEW_PERIOD_DEFAULT));
  const appliedSavedRef = useRef(false);

  const [graphData,setGraphData] = useState({
    int_open:0,int_closed:0,ext_open:0,ext_closed:0,rek_open:0,rek_closed:0
  });

  useEffect(() => {
    if (appliedSavedRef.current) return;
    if (!state.user_mask_filters?.length) return;
    appliedSavedRef.current = true;
    const mask = state.user_mask_filters.find((m) => m.page === PREVIEW_PAGE);
    const attrs = mask?.attributes;
    const parsed = typeof attrs === 'string' ? (() => { try { return parseSavedPeriod(JSON.parse(attrs)); } catch { return null; } })() : parseSavedPeriod(attrs);
    if (parsed) {
      setTimePeriod(parsed.timePeriod);
      setDate(parsed.date ?? (parsed.timePeriod === 'Všetko' ? null : defaultDateForPeriod(parsed.timePeriod)));
      setDateProps(datePickerPropsForPeriod(parsed.timePeriod));
    }
  }, [state.user_mask_filters]);

  const savePeriodToDb = () => {
    setUserMaskFilter(PREVIEW_PAGE, 'set_attributes', null, null, { preview_period: serializePeriod(timePeriod, date) });
  };


  const componentRef = useRef(null);
  const intRef = useRef(null);
  const extRef = useRef(null);
  const rekRef = useRef(null);
  const otkCompaniesRef = useRef(null);
  const [legendHidden, setLegendHidden] = useState({ int: [], ext: [], rek: [], otkCompanies: [] });
  /** Last successful preview_stats model — also mirrored into originalDataRef for legend hide/show. */
  const [previewModel, setPreviewModel] = useState(() => ({
    int: [...EMPTY_OTK_PREVIEW_MODEL.int],
    ext: [...EMPTY_OTK_PREVIEW_MODEL.ext],
    rek: [...EMPTY_OTK_PREVIEW_MODEL.rek],
    otkCompanies: [...EMPTY_OTK_PREVIEW_MODEL.otkCompanies],
  }));
  const originalDataRef = useRef({
    int: [...EMPTY_OTK_PREVIEW_MODEL.int],
    ext: [...EMPTY_OTK_PREVIEW_MODEL.ext],
    rek: [...EMPTY_OTK_PREVIEW_MODEL.rek],
    otkCompanies: [...EMPTY_OTK_PREVIEW_MODEL.otkCompanies],
  });
  const statsRequestIdRef = useRef(0);

  const module_settings = state.module_settings || [];
  const COLOR_TO_HEX = useRef({ red: '#b91c1c', orange: '#ca8a04', green: '#15803d', gray: '#6b7280', blue: '#2563eb', black: '#171717' }).current;
  const openColorKey = getModuleSettingValue(module_settings, 'otk', 'otk_graph_open_color', 'green');
  const closedColorKey = getModuleSettingValue(module_settings, 'otk', 'otk_graph_closed_color', 'gray');
  const openClosedColors = useMemo(() => [
    COLOR_TO_HEX[openColorKey] || '#15803d',
    COLOR_TO_HEX[closedColorKey] || '#6b7280',
  ], [openColorKey, closedColorKey]);
  const openClosedColorsRef = useRef(openClosedColors);
  openClosedColorsRef.current = openClosedColors;

  const buildPreviewStatsParams = (timePeriodVal, dateVal) => {
    const params = new URLSearchParams();
    params.set('timePeriod', timePeriodVal || 'Všetko');
    if (timePeriodVal && timePeriodVal !== 'Všetko' && dateVal != null) {
      if (Array.isArray(dateVal)) {
        params.set('date', JSON.stringify(dateVal.map(d => (d && d instanceof Date) ? toLocalDateString(d) : d)));
      } else if (dateVal && dateVal instanceof Date) {
        params.set('date', toLocalDateString(dateVal));
      } else {
        params.set('date', String(dateVal));
      }
    }
    return params.toString();
  };

  const [statsLoading, setStatsLoading] = useState(true);
  const fetchPreviewStats = useCallback((timePeriodVal, dateVal) => {
    const requestId = ++statsRequestIdRef.current;
    setStatsLoading(true);
    const qs = buildPreviewStatsParams(timePeriodVal, dateVal);
    getRequest(`/api/otk/preview_stats?${qs}`)
      .then((res) => {
        if (requestId !== statsRequestIdRef.current) return;
        const model = normalizeOtkPreviewStats(res);
        // Sync ref before state so same render sees authoritative zeros (never previous 5/4 via ??).
        originalDataRef.current = {
          int: [...model.int],
          ext: [...model.ext],
          rek: [...model.rek],
          otkCompanies: [...model.otkCompanies],
        };
        setPreviewModel(model);
        setLegendHidden({ int: [], ext: [], rek: [], otkCompanies: [] });
        const colors = openClosedColorsRef.current;
        const base = { labels: [t('Status.OpenPlural'), t('Status.ClosedNeut')], backgroundColor: colors, hoverBackgroundColor: colors };
        setIntData({ ...base, datasets: [{ ...base, data: [...model.int] }] });
        setExtData({ ...base, datasets: [{ ...base, data: [...model.ext] }] });
        setRekData({ ...base, datasets: [{ ...base, data: [...model.rek] }] });
        setOtkCompaniesData((prev) => ({
          ...prev,
          labels: [t('Common.Expiry.After'), t('Common.Expiry.Days15Short'), t('Common.Expiry.Days30Short'), t('Common.Status.OK')],
          datasets: [{ ...prev.datasets[0], data: [...model.otkCompanies] }],
        }));
      })
      .catch(() => {
        if (requestId !== statsRequestIdRef.current) return;
      })
      .finally(() => {
        if (requestId !== statsRequestIdRef.current) return;
        setStatsLoading(false);
      });
  }, [t]);

  const certColor0 = getModuleSettingValue(module_settings, 'otk', 'otk_graph_docs_0', 'red');
  const certColor15 = getModuleSettingValue(module_settings, 'otk', 'otk_graph_docs_15', 'orange');
  const certColor30 = getModuleSettingValue(module_settings, 'otk', 'otk_graph_docs_30', 'orange');
  const certColorOk = getModuleSettingValue(module_settings, 'otk', 'otk_graph_docs_ok', 'green');
  const certColors = useMemo(() => [
    COLOR_TO_HEX[certColor0] || '#b91c1c',
    COLOR_TO_HEX[certColor15] || '#ca8a04',
    COLOR_TO_HEX[certColor30] || '#eab308',
    COLOR_TO_HEX[certColorOk] || '#15803d',
  ], [certColor0, certColor15, certColor30, certColorOk]);

  const toggleLegendItem = (chartKey, index, chartRef, sourceData) => () => {
    const chartApi = chartRef?.current;
    const ch = chartApi?.getChart?.();
    if (!ch) return;
    const originals = originalDataRef.current[chartKey];
    if (!Array.isArray(originals)) return;
    const currentlyHidden = legendHidden[chartKey]?.[index];
    const nextHidden = !currentlyHidden;
    const hiddenArr = legendHidden[chartKey] ?? [];
    const newData = originals.map((val, i) => {
      const isHidden = i === index ? nextHidden : hiddenArr[i];
      return isHidden ? 0 : (Number(val) || 0);
    });
    const ds = ch.data.datasets[0];
    ds.data = [...newData];
    const meta = ch.getDatasetMeta?.(0);
    if (meta && Array.isArray(meta._parsed)) {
      newData.forEach((v, i) => { meta._parsed[i] = v; });
    }
    ch.update();
    setLegendHidden((prev) => {
      const arr = prev[chartKey] ?? [];
      const next = [...arr];
      next[index] = nextHidden;
      return { ...prev, [chartKey]: next };
    });
  };

    const [intData,setIntData] = useState({
        labels: [t('Status.OpenPlural'), t('Status.ClosedNeut')],
        datasets: [{
                data: [0, 0],
                backgroundColor: ['#15803d', '#6b7280'],
                hoverBackgroundColor: ['#15803d', '#6b7280'],
            }]
    });

    const [extData,setExtData] = useState({
        labels: [t('Status.OpenPlural'), t('Status.ClosedNeut')],
        datasets: [{
            data: [0, 0],
            backgroundColor: ['#15803d', '#6b7280'],
            hoverBackgroundColor: ['#15803d', '#6b7280'],
        }]
    })

    const [rekData,setRekData] = useState({
        labels: [t('Status.OpenPlural'), t('Status.ClosedNeut')],
        datasets:  [{
            data: [0, 0],
            backgroundColor: ['#15803d', '#6b7280'],
            hoverBackgroundColor: ['#15803d', '#6b7280'],
        }]
    })

    const [otkCompaniesData, setOtkCompaniesData] = useState({
        labels: [t('Common.Expiry.After'), t('Common.Expiry.Days15Short'), t('Common.Expiry.Days30Short'), t('Common.Status.OK')],
        datasets: [{
            data: [0, 0, 0, 0],
            backgroundColor: certColors,
            hoverBackgroundColor: certColors,
        }]
    });

    useEffect(() => {
      fetchPreviewStats(timePeriod, date);
    }, [timePeriod, date, fetchPreviewStats]);

    const intOptions = {
        plugins: { legend: { display: false } },
        onClick: function (evt, item) {
            if(item[0].index==0){
                dispatch({type:'create_new_tab',name: t('Otk.InternalError'), translationKey: 'Otk.InternalError', legacyLabel: t('Otk.InternalError'),page:'ZoznamInt',props:{preview: 'open',timePeriod: timePeriod,date: date},mask:'qlty002'});
            }
            else{
                dispatch({type:'create_new_tab',name: t('Otk.InternalError'), translationKey: 'Otk.InternalError', legacyLabel: t('Otk.InternalError'),page:'ZoznamInt',props:{preview: 'closed',timePeriod: timePeriod,date: date},mask:'qlty002'});
            }
        }
    };

    const extOptions = {
        plugins: { legend: { display: false } },
        onClick: function (evt, item) {
            if(item[0].index==0){
                dispatch({type:'create_new_tab',name: t('Otk.ExternalError'), translationKey: 'Otk.ExternalError', legacyLabel: t('Otk.ExternalError'),page:'ZoznamExt',props:{preview: 'open',timePeriod: timePeriod,date: date},mask:'qlty004'});
            }
            else{
                dispatch({type:'create_new_tab',name: t('Otk.ExternalError'), translationKey: 'Otk.ExternalError', legacyLabel: t('Otk.ExternalError'),page:'ZoznamExt',props:{preview: 'closed',timePeriod: timePeriod,date: date},mask:'qlty004'});
            }
        }
    };

    const rekOptions = {
        plugins: { legend: { display: false } },
        onClick: function (evt, item) {
            if(item[0].index==0){
                dispatch({type:'create_new_tab',name: t('Otk.Complaint'), translationKey: 'Otk.Complaint', legacyLabel: t('Otk.Complaint'),page:'ZoznamRek',props:{preview: 'open',timePeriod: timePeriod,date: date},mask:'qlty006'});
            }
            else{
                dispatch({type:'create_new_tab',name: t('Otk.Complaint'), translationKey: 'Otk.Complaint', legacyLabel: t('Otk.Complaint'),page:'ZoznamRek',props:{preview: 'closed',timePeriod: timePeriod,date: date},mask:'qlty006'});
            }
        }
    };


    const plugin = {
        id: 'emptyDoughnut',
        afterDraw(chart) {
            const data = chart.data?.datasets?.[0]?.data ?? [];
            const count = data.reduce((a, b) => a + (b ?? 0), 0);
            if (count < 1) {
                const ctx = chart.ctx;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.font = '20px Arial';
                ctx.fillStyle = '#9ca3af';
                ctx.fillText(t('Common.NoRecords'), chart.width / 2, chart.height / 2);
            }
        },
    };

        const openCertDrilldown = useCallback((previewSeg) => {
            dispatch({
                type: 'create_new_tab',
                name: t('Common.ByStatus'), translationKey: 'Common.ByStatus', legacyLabel: t('Common.ByStatus'),
                page: 'OtkCompanyCertStateList',
                props: { preview: previewSeg, timePeriod, date },
                mask: 'qlty030',
            });
        }, [dispatch, t, timePeriod, date]);

        const otkCompaniesOptions = useMemo(() => ({
            plugins: { legend: { display: false } },
            onClick: function (evt, item) {
                if (!item?.length) return;
                const segs = ['after', '15', '30', 'others'];
                openCertDrilldown(segs[item[0].index]);
            }
        }), [openCertDrilldown]);

    function changeTimePeriod(time_period){
        setStatsLoading(true);
        setTimePeriod(time_period);
        setDate(defaultDateForPeriod(time_period));
        setDateProps(datePickerPropsForPeriod(time_period));
    }

    function showResult(date){
        setDate(date);
    }

    const handleRefresh = () => {
        if (timePeriod === 'Všetko') {
            changeTimePeriod('Všetko');
            fetchPreviewStats('Všetko', null);
            return;
        }
        if (date) showResult(date);
        else showResult(new Date());
    };

    // Po načítaní obdobia z masky alebo zmene timePeriod/date prepočítať grafy (showResult len nastavuje state, dáta sa prepočítajú tu)
    useEffect(() => {
        if (timePeriod === 'Všetko' || date == null) return;
        showResult(date);
    }, [timePeriod, date]);

    return (
        <div className="h-[calc(100vh-137px)] overflow-y-auto">
        <div className="lg:grid grid-cols-3 gap-1">
            <div></div>
            <div>
                <div className="flex items-center gap-3 mt-4">
                    <label htmlFor="time_period" className="text-xl font-semibold text-gray-800 shrink-0">{t('Common.Period')}:</label>
                    <select
                        name="time_period"
                        value={timePeriod}
                        onChange={(e) => changeTimePeriod(e.target.value)}
                        className="bg-white flex-1 min-w-0 px-4 py-2 text-gray-700 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                    >
                    <option value="Všetko">{t('Common.Period.All')}</option>
                    <option value="Za deň">{t('Common.Period.Day')}</option>
                    <option value="Za mesiac">{t('Common.Period.Month')}</option>
                    <option value="Za rok">{t('Common.Period.Year')}</option>
                    <option value="Vlastné">{t('Common.Period.Custom')}</option>
                </select>
                    <button
                        type="button"
                        onClick={handleRefresh}
                        title={t('Common.RefreshData')}
                        className="flex items-center justify-center w-10 h-10 shrink-0 rounded-md border border-gray-300 bg-gray-600 hover:bg-gray-700 text-white transition-colors"
                    >
                        <RefreshIcon />
                    </button>
                    <button
                        type="button"
                        onClick={savePeriodToDb}
                        title={t('Common.SaveSelectedPeriod')}
                        className="shrink-0 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring focus:ring-gray-200"
                    >
                        {t('Common.SaveMask')}
                    </button>
                </div>
            </div>
            <div></div>
            <div></div>
            <div className="flex-auto">
                <Calendar id="buttondisplay" style={{border: '1px solid #e5e7eb',transition: 'background-color 0.2s, color 0.2s, border-color 0.2s, box-shadow 0.2s',borderRadius: '6px'}} 
                value={date} onChange={(e) => showResult(e.target.value)} showIcon className="w-full" visible={true} locale={calendarLocale} {...dateProps} hideOnRangeSelection />
            </div>
            <div></div>
        </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4">
  <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
    <h2 className="text-center text-xl font-semibold mb-4">{t('OTKEvaluation.InterneChyby')}</h2>
    <div className="w-full max-w-[420px] h-[420px] mx-auto flex items-center justify-center">
      {statsLoading ? <Skeleton shape="circle" size="320px" className="flex-shrink-0" /> : <Chart type="doughnut" data={intData} options={intOptions} className="w-full h-full" ref={intRef} plugins={[plugin]} />}
    </div>
    {!statsLoading && (
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-700">
      {intData.labels?.map((label, i) => {
        const count = otkPreviewLegendCount(previewModel.int, i);
        return (
        <button key={i} type="button" data-testid={`otk-preview-legend-int-${i}`} onClick={() => {
          if (count > 0) {
            dispatch({type:'create_new_tab',name: t('Otk.InternalError'), translationKey: 'Otk.InternalError', legacyLabel: t('Otk.InternalError'),page:'ZoznamInt',props:{preview: i === 0 ? 'open' : 'closed',timePeriod,date},mask:'qlty002'});
          } else {
            toggleLegendItem('int', i, intRef, intData)();
          }
        }} className="inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 text-left hover:opacity-80" style={{ textDecoration: legendHidden.int?.[i] ? 'line-through' : undefined, opacity: legendHidden.int?.[i] ? 0.5 : 1 }}>
          <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: intData.datasets?.[0]?.backgroundColor?.[i] ?? '#6b7280' }} />
          {label} ({count})
        </button>
        );
      })}
    </div>
    )}
    <div className="text-center font-semibold mt-4">{statsLoading ? <Skeleton width="8rem" height="1.5rem" className="mx-auto" /> : t('Common.TotalCount', { count: otkPreviewTotal(previewModel.int) })}</div>
  </div>
  <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
    <h2 className="text-center text-xl font-semibold mb-4">{t('OTKEvaluation.ExterneChyby')}</h2>
    <div className="w-full max-w-[420px] h-[420px] mx-auto flex items-center justify-center">
      {statsLoading ? <Skeleton shape="circle" size="320px" className="flex-shrink-0" /> : <Chart ref={extRef} type="doughnut" data={extData} options={extOptions} className="w-full h-full" plugins={[plugin]} />}
    </div>
    {!statsLoading && (
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-700">
      {extData.labels?.map((label, i) => {
        const count = otkPreviewLegendCount(previewModel.ext, i);
        return (
        <button key={i} type="button" data-testid={`otk-preview-legend-ext-${i}`} onClick={() => {
          if (count > 0) {
            dispatch({type:'create_new_tab',name: t('Otk.ExternalError'), translationKey: 'Otk.ExternalError', legacyLabel: t('Otk.ExternalError'),page:'ZoznamExt',props:{preview: i === 0 ? 'open' : 'closed',timePeriod,date},mask:'qlty004'});
          } else {
            toggleLegendItem('ext', i, extRef, extData)();
          }
        }} className="inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 text-left hover:opacity-80" style={{ textDecoration: legendHidden.ext?.[i] ? 'line-through' : undefined, opacity: legendHidden.ext?.[i] ? 0.5 : 1 }}>
          <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: extData.datasets?.[0]?.backgroundColor?.[i] ?? '#6b7280' }} />
          {label} ({count})
        </button>
        );
      })}
    </div>
    )}
    <div className="text-center font-semibold mt-4">{statsLoading ? <Skeleton width="8rem" height="1.5rem" className="mx-auto" /> : t('Common.TotalCount', { count: otkPreviewTotal(previewModel.ext) })}</div>
  </div>
  <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
    <h2 className="text-center text-xl font-semibold mb-4">{t('OTKEvaluation.Reklamacie')}</h2>
    <div className="w-full max-w-[420px] h-[420px] mx-auto flex items-center justify-center">
      {statsLoading ? <Skeleton shape="circle" size="320px" className="flex-shrink-0" /> : <Chart ref={rekRef} type="doughnut" data={rekData} options={rekOptions} className="w-full h-full" plugins={[plugin]} />}
    </div>
    {!statsLoading && (
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-700">
      {rekData.labels?.map((label, i) => {
        const count = otkPreviewLegendCount(previewModel.rek, i);
        return (
        <button key={i} type="button" data-testid={`otk-preview-legend-rek-${i}`} onClick={() => {
          if (count > 0) {
            dispatch({type:'create_new_tab',name: t('Otk.Complaint'), translationKey: 'Otk.Complaint', legacyLabel: t('Otk.Complaint'),page:'ZoznamRek',props:{preview: i === 0 ? 'open' : 'closed',timePeriod,date},mask:'qlty006'});
          } else {
            toggleLegendItem('rek', i, rekRef, rekData)();
          }
        }} className="inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 text-left hover:opacity-80" style={{ textDecoration: legendHidden.rek?.[i] ? 'line-through' : undefined, opacity: legendHidden.rek?.[i] ? 0.5 : 1 }}>
          <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: rekData.datasets?.[0]?.backgroundColor?.[i] ?? '#6b7280' }} />
          {label} ({count})
        </button>
        );
      })}
    </div>
    )}
    <div className="text-center font-semibold mt-4">{statsLoading ? <Skeleton width="8rem" height="1.5rem" className="mx-auto" /> : t('Common.TotalCount', { count: otkPreviewTotal(previewModel.rek) })}</div>
  </div>
  <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
    <h2 className="text-center text-xl font-semibold mb-4">{t('Otk.PartnerDocumentations')}</h2>
    <div className="w-full max-w-[420px] h-[420px] mx-auto flex items-center justify-center">
      {statsLoading ? <Skeleton shape="circle" size="320px" className="flex-shrink-0" /> : <Chart ref={otkCompaniesRef} type="doughnut" data={otkCompaniesData} options={otkCompaniesOptions} className="w-full h-full" plugins={[plugin]} />}
    </div>
    {!statsLoading && (
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-700">
      {otkCompaniesData.labels?.map((label, i) => {
        const count = otkPreviewLegendCount(previewModel.otkCompanies, i);
        const previewSeg = i === 0 ? 'after' : i === 1 ? '15' : i === 2 ? '30' : 'others';
        return (
        <button key={i} type="button" data-testid={`otk-preview-legend-certs-${i}`} onClick={() => {
          if (count > 0) {
            openCertDrilldown(previewSeg);
          } else {
            toggleLegendItem('otkCompanies', i, otkCompaniesRef, otkCompaniesData)();
          }
        }} className="inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 text-left hover:opacity-80" style={{ textDecoration: legendHidden.otkCompanies?.[i] ? 'line-through' : undefined, opacity: legendHidden.otkCompanies?.[i] ? 0.5 : 1 }}>
          <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: otkCompaniesData.datasets?.[0]?.backgroundColor?.[i] ?? '#6b7280' }} />
          {label} ({count})
        </button>
        );
      })}
    </div>
    )}
    <div className="text-center font-semibold mt-4">{statsLoading ? <Skeleton width="8rem" height="1.5rem" className="mx-auto" /> : t('Common.TotalCount', { count: otkPreviewTotal(previewModel.otkCompanies) })}</div>
  </div>
</div>
</div>
    );
}